import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Provider } from 'react-redux'
import { store } from '../store/store'
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchCurrentUser } from '../store/authSlice';

import Layout from "./Layout";
import Home from "./Home";
import Search from "./Search";
import RecipeIndex from "./recipes/RecipeIndex";
import Ingredients from "./ingredients/IngredientsIndex";
import NewRecipeForm from './recipes/RecipeNew';
import RecipeEdit from './recipes/RecipeEdit';
import RecipeShow from './recipes/RecipeShow';
import ShoppingList from './ShoppingList';
import Login from './Login';
import Account from './Account';
import AdminLayout from './admin/AdminLayout'
import AdminUsers from './admin/AdminUsers'
import AdminStores from './admin/AdminStores'
import AdminProducts from './admin/AdminProducts';

import "../channels/recipe_channel"

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, status } = useAppSelector((s) => s.auth);
  const location = useLocation();

  if (status === 'loading') return null;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return <>{children}</>;
};

const RequireAdmin: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, status } = useAppSelector((s) => s.auth);
  const location = useLocation();

  if (status === 'loading') return null;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (!user.is_admin) return <Navigate to="/" replace />;
  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchCurrentUser());
  }, [dispatch]);

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Layout />}>
        <Route index element={<Home />} />
        <Route path="search" element={<Search />} />
        <Route path="recipes" element={<RecipeIndex scope="all" />} />
        <Route path="recipes/mine" element={<RequireAuth><RecipeIndex scope="mine" /></RequireAuth>} />
        <Route path="recipes/saved" element={<RequireAuth><RecipeIndex scope="saved" /></RequireAuth>} />
        <Route path="recipes/new" element={<RequireAuth><NewRecipeForm /></RequireAuth>} />
        <Route path="recipes/:id" element={<RecipeShow />} />
        <Route path="recipes/:id/edit" element={<RequireAuth><RecipeEdit /></RequireAuth>} />
        <Route path="foods" element={<Ingredients />} />
        <Route path="ingredients" element={<Navigate to="/foods" replace />} />
        <Route path="shopping-list" element={<ShoppingList />} />
        <Route path="account" element={<RequireAuth><Account /></RequireAuth>} />
        <Route path="admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
          <Route index element={<Navigate to="users" replace />} />
          <Route path="users"    element={<AdminUsers />} />
          <Route path="stores"   element={<AdminStores />} />
          <Route path="products" element={<AdminProducts />} />
        </Route>
      </Route>
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <AppRoutes />
    </Provider>
  );
};

export default App;
